import { BadgeCheck, Ban, UserCheck } from "lucide-react";
import { PLAN_COLORS } from "@/components/admin/adminShared";
import { isStaff } from "@/lib/roles";

function UserActions({ user, busy, onStatus, onPromote, selfId }) {
  if (isStaff(user.role) || user.user_id === selfId) return null;
  return (
    <div className="flex items-center justify-end gap-1.5">
      {user.status !== "active" ? (
        <button onClick={() => onStatus(user.user_id, "active")} disabled={busy === user.user_id + "active"} data-testid={`admin-activate-${user.user_id}`} className="text-xs px-2 py-1 rounded border border-green-200 text-green-700 hover:bg-green-50 flex items-center gap-1"><UserCheck size={12} /> Activate</button>
      ) : (
        <button onClick={() => onStatus(user.user_id, "suspended")} disabled={busy === user.user_id + "suspended"} data-testid={`admin-suspend-${user.user_id}`} className="text-xs px-2 py-1 rounded border border-amber-200 text-amber-700 hover:bg-amber-50">Suspend</button>
      )}
      {user.status !== "banned" && (
        <button onClick={() => onStatus(user.user_id, "banned")} disabled={busy === user.user_id + "banned"} data-testid={`admin-ban-${user.user_id}`} className="text-xs px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 flex items-center gap-1"><Ban size={12} /> Ban</button>
      )}
      <button onClick={() => onPromote(user.user_id, "admin")} data-testid={`admin-promote-${user.user_id}`} className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50">Make admin</button>
    </div>
  );
}

const statusColor = (s) => (s === "banned" ? "text-red-600" : s === "suspended" ? "text-amber-600" : "text-green-600");

// Internal provenance badge (account origin) — NOT KYB/TradeMatch verification.
const REAL_ORIGINS = ["real_operator", "self_registered"];
const SYNTH_ORIGINS = ["automated_test", "demo_seed", "system_service"];
function OriginBadge({ origin }) {
  const isReal = REAL_ORIGINS.includes(origin);
  const isSynth = SYNTH_ORIGINS.includes(origin);
  const label = isReal ? "Real account" : isSynth ? "Synthetic-test" : "Unclassified";
  const cls = isReal ? "bg-green-100 text-green-700" : isSynth ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500";
  return (
    <span data-testid="user-origin-badge" title="Account origin only — NOT KYB or TradeMatch verification"
      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>{label}</span>
  );
}

const MARKABLE_ORIGINS = ["self_registered", "unclassified", undefined, null];
function MarkTestButton({ user, busy, onMarkTest, canDesignate }) {
  if (!canDesignate || isStaff(user.role) || !MARKABLE_ORIGINS.includes(user.account_origin)) return null;
  return (
    <button onClick={() => onMarkTest(user)} disabled={busy === user.user_id + "marktest"} data-testid={`admin-mark-test-${user.user_id}`}
      className="text-xs px-2 py-1 rounded border border-amber-200 text-amber-700 hover:bg-amber-50 mr-1.5">Mark as test</button>
  );
}

export default function UserRow({ user, busy, onStatus, onPromote, selfId, onMarkTest, canDesignate }) {
  return (
    <tr className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50" data-testid={`admin-user-row-${user.user_id}`}>
      <td className="px-4 py-3">
        <div className="font-medium text-[#0A0F1C] flex items-center gap-1.5">{user.name}{user.role === "admin" && <span className="text-[10px] bg-[#C2410C] text-white px-1.5 py-0.5 rounded">ADMIN</span>}</div>
        <div className="text-xs text-slate-400 font-mono flex items-center gap-1.5">{user.email} <OriginBadge origin={user.account_origin} /></div>
      </td>
      <td className="px-4 py-3 text-slate-600">{user.org_name || "—"} {user.verified && <BadgeCheck size={13} className="inline text-[#2563EB]" />}</td>
      <td className="px-4 py-3"><span className="text-xs font-medium px-2 py-0.5 rounded-full capitalize" style={{ background: (PLAN_COLORS[user.plan] || "#94A3B8") + "20", color: PLAN_COLORS[user.plan] || "#64748B" }}>{user.plan}</span></td>
      <td className="px-4 py-3"><span className={`text-xs font-medium ${statusColor(user.status)}`}>{user.status || "active"}</span></td>
      <td className="px-4 py-3 font-mono text-slate-600">{user.trust_score}</td>
      <td className="px-4 py-3"><div className="flex items-center justify-end">
        <MarkTestButton user={user} busy={busy} onMarkTest={onMarkTest} canDesignate={canDesignate} />
        <UserActions user={user} busy={busy} onStatus={onStatus} onPromote={onPromote} selfId={selfId} />
      </div></td>
    </tr>
  );
}
